import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatBRL, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CopyTrackButton from "@/components/CopyTrackButton";
import { Loader2, Mail, MailWarning, Send, Phone, Trash2, ChevronDown, ChevronUp, ShoppingBag, UserCheck, Copy, AtSign } from "lucide-react";

// Toast durations (ms)
const TOAST_LONG_MS = 6000;
const TOAST_MEDIUM_MS = 5000;
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const statusMap = {
  paid: { label: "Pago", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  pending: { label: "Pendente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  cancelled: { label: "Cancelado", cls: "bg-stone-200 text-stone-700 border-stone-300" },
};

export default function ManageOrders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch {
      toast.error("Erro ao carregar pedidos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/orders/${id}`, { status });
      setOrders((s) => s.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Status atualizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este pedido?")) return;
    try {
      await api.delete(`/orders/${id}`);
      setOrders((s) => s.filter((o) => o.id !== id));
      toast.success("Pedido excluído");
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const resendEmail = async (id) => {
    try {
      toast.info("Reenviando e-mail...");
      const { data } = await api.post(`/orders/${id}/resend-email`);
      if (data?.ok === false) {
        setOrders((s) => s.map((o) => (o.id === id ? { ...o, email_status: "failed", email_error: data.raw_error || data.error || "" } : o)));
        toast.error(data.error || "Falha no reenvio");
        return;
      }
      setOrders((s) => s.map((o) => (o.id === id ? { ...o, email_status: "sent", email_sent_at: data.email_sent_at, email_error: "" } : o)));
      toast.success("E-mail reenviado com sucesso");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no reenvio");
    }
  };

  const [syncing, setSyncing] = useState(false);
  const [dedupRunning, setDedupRunning] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const syncSubscriptions = async () => {
    if (!window.confirm("Migrar TODOS os pedidos para inscrições quando o e-mail ainda não estiver inscrito? Cada pedido elegível será movido para a base de inscrições (mantendo o mesmo comprovante).")) {
      return;
    }
    setSyncing(true);
    try {
      const { data } = await api.post("/orders/sync-subscriptions");
      const { checked = 0, moved = 0, skipped_existing = 0 } = data || {};
      toast.success(
        `Sincronização concluída — ${moved} inscrito(s), ${skipped_existing} já existente(s), ${checked} verificado(s)`,
        { duration: TOAST_LONG_MS }
      );
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const deduplicateSubscriptions = async () => {
    if (!window.confirm("Apagar inscrições duplicadas por e-mail? Mantém automaticamente a mais completa (status pago → mais itens → total maior → mais antiga). Ação não pode ser desfeita.")) {
      return;
    }
    setDedupRunning(true);
    try {
      const { data } = await api.post("/subscriptions/deduplicate");
      const { duplicate_groups = 0, removed = 0 } = data || {};
      if (removed === 0) {
        toast.success("Nenhuma duplicidade encontrada — sua base está limpa!", { duration: TOAST_MEDIUM_MS });
      } else {
        toast.success(
          `${removed} duplicata(s) removida(s) em ${duplicate_groups} e-mail(s). Base normalizada.`,
          { duration: TOAST_LONG_MS }
        );
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao deduplicar");
    } finally {
      setDedupRunning(false);
    }
  };

  const renameCustomerEmail = async () => {
    const oldEmail = window.prompt("E-mail atual (com typo):")?.trim();
    if (!oldEmail) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(oldEmail)) {
      toast.error("E-mail atual inválido");
      return;
    }
    const newEmail = window.prompt(`Novo e-mail para substituir "${oldEmail}":`)?.trim();
    if (!newEmail) return;
    if (!emailRegex.test(newEmail)) {
      toast.error("Novo e-mail inválido");
      return;
    }
    if (oldEmail.toLowerCase() === newEmail.toLowerCase()) {
      toast.info("Os e-mails são idênticos — nada a fazer");
      return;
    }
    if (!window.confirm(`Confirmar a alteração?\n\nDe: ${oldEmail}\nPara: ${newEmail}\n\nTodos os pedidos e inscrições serão atualizados.`)) {
      return;
    }
    setRenaming(true);
    try {
      const { data } = await api.post("/admin/rename-email", {
        old_email: oldEmail,
        new_email: newEmail,
      });
      const total = (data.subscriptions_updated || 0) + (data.orders_updated || 0);
      if (total === 0) {
        toast.info(`Nenhum registro encontrado com "${oldEmail}".`);
      } else {
        toast.success(
          `${total} registro(s) atualizados — ${data.subscriptions_updated} inscrição(ões), ${data.orders_updated} pedido(s)`,
          { duration: TOAST_LONG_MS }
        );
        await load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao renomear e-mail");
    } finally {
      setRenaming(false);
    }
  };

  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter !== "all" && o.status !== filter) return false;
      if (!q) return true;
      return (
        (o.customer_name || "").toLowerCase().includes(q) ||
        (o.customer_email || "").toLowerCase().includes(q) ||
        (o.customer_phone || "").toLowerCase().includes(q) ||
        (o.city || "").toLowerCase().includes(q)
      );
    });
  }, [orders, filter, searchQuery]);
  const totalRevenue = orders.filter((o) => o.status === "paid").reduce((s, o) => s + o.total, 0);

  return (
    <div className="space-y-8" data-testid="manage-orders">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-700 font-semibold">Pedidos</div>
          <h1 className="font-display text-4xl font-bold mt-2">Solicitações de compra</h1>
          <p className="text-stone-600 mt-2">
            {orders.length} pedido(s) · {formatBRL(totalRevenue)} confirmado
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={syncSubscriptions}
                disabled={syncing || dedupRunning || renaming}
                data-testid="sync-subscriptions-btn"
                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              >
                {syncing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserCheck className="h-4 w-4 mr-2" />
                )}
                Sincronizar inscrições
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deduplicateSubscriptions}
                disabled={syncing || dedupRunning || renaming}
                data-testid="dedup-subscriptions-btn"
                className="border-rose-300 text-rose-800 hover:bg-rose-50"
              >
                {dedupRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Copy className="h-4 w-4 mr-2" />
                )}
                Deduplicar inscrições
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={renameCustomerEmail}
                disabled={syncing || dedupRunning || renaming}
                data-testid="rename-email-btn"
                className="border-sky-300 text-sky-800 hover:bg-sky-50"
              >
                {renaming ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <AtSign className="h-4 w-4 mr-2" />
                )}
                Renomear e-mail
              </Button>
            </>
          )}
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nome, e-mail, telefone ou cidade..."
            className="w-72"
            data-testid="orders-search-input"
          />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="orders-filter">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="paid">Pagos</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
      ) : visible.length === 0 ? (
        <Card className="border-dashed border-stone-300 rounded-2xl">
          <CardContent className="py-16 text-center">
            <ShoppingBag className="h-10 w-10 text-stone-400 mx-auto" />
            <div className="font-display text-xl mt-4 text-stone-700">Nenhum pedido</div>
            <p className="text-stone-500 text-sm mt-2">Os pedidos aparecerão aqui assim que forem realizados.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {visible.map((o) => {
            const st = statusMap[o.status] || statusMap.pending;
            const isOpen = expanded[o.id];
            return (
              <Card key={o.id} className="border-stone-200 rounded-2xl overflow-hidden" data-testid={`order-${o.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-display text-xl font-semibold" data-testid={`order-name-${o.id}`}>
                          {o.customer_name}
                        </div>
                        <Badge className={`${st.cls} hover:${st.cls}`} data-testid={`order-status-${o.id}`}>
                          {st.label}
                        </Badge>
                        {o.source === "subscription" && (
                          <Badge
                            className="bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100 text-[10px] uppercase tracking-widest"
                            data-testid={`order-source-${o.id}`}
                          >
                            Inscrição{o.city ? ` · ${o.city}` : ""}
                          </Badge>
                        )}
                        {o.email_status === "failed" && (
                          <Badge
                            className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                            data-testid={`order-email-failed-${o.id}`}
                            title={o.email_error || "Falha no envio do e-mail"}
                          >
                            <MailWarning className="h-3 w-3" /> E-mail falhou
                          </Badge>
                        )}
                        {o.email_status === "sent" && (
                          <Badge
                            className="bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-50 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                            data-testid={`order-email-sent-${o.id}`}
                            title={o.email_sent_at ? `Enviado em ${new Date(o.email_sent_at).toLocaleString("pt-BR")}` : "E-mail enviado"}
                          >
                            <Mail className="h-3 w-3" /> E-mail enviado
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-stone-600">
                        <span className="flex items-center gap-1.5">
                          <Mail className="h-3.5 w-3.5" /> {o.customer_email}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5" /> {o.customer_phone}
                        </span>
                        <span className="text-stone-400">
                          {new Date(o.created_at).toLocaleString("pt-BR")}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-widest text-stone-500">Total</div>
                      <div className="font-display text-2xl font-bold brand-text">{formatBRL(o.total)}</div>
                      <div className="text-xs text-stone-500 mt-1">
                        {o.items.reduce((s, i) => s + i.quantity, 0)} item(ns)
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2 items-center">
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)} disabled={!isAdmin}>
                      <SelectTrigger className="w-40 disabled:opacity-60" data-testid={`order-status-select-${o.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="paid">Pago</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpanded((s) => ({ ...s, [o.id]: !s[o.id] }))}
                      data-testid={`order-toggle-${o.id}`}
                    >
                      {isOpen ? <ChevronUp className="h-3.5 w-3.5 mr-1.5" /> : <ChevronDown className="h-3.5 w-3.5 mr-1.5" />}
                      {isOpen ? "Ocultar itens" : "Ver itens"}
                    </Button>
                    <CopyTrackButton
                      txid={o.txid}
                      customerName={o.customer_name}
                      customerPhone={o.customer_phone}
                      kind={o.source === "subscription" ? "subscription" : "order"}
                      testidPrefix={`order-${o.id}`}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resendEmail(o.id)}
                      disabled={!isAdmin || !o.customer_email}
                      className="text-sky-800 border-sky-200 hover:bg-sky-50 disabled:opacity-40"
                      data-testid={`order-resend-email-${o.id}`}
                    >
                      <Send className="h-3.5 w-3.5 mr-1.5" /> Reenviar e-mail
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => remove(o.id)}
                      disabled={!isAdmin}
                      className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800 disabled:opacity-40"
                      data-testid={`order-delete-${o.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {isOpen && (
                    <div className="mt-5 pt-5 border-t border-stone-200 space-y-2">
                      {o.items.map((it, idx) => (
                        <div key={`${o.id}-${idx}-${it.item_id || it.name}`} className="flex items-center justify-between text-sm">
                          <span className="text-stone-700 flex flex-wrap items-center gap-1.5">
                            <strong>{it.quantity}×</strong> {it.name}
                            {it.size && (
                              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200">
                                {it.size}
                              </span>
                            )}
                            {it.variant && (
                              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                {it.variant}
                              </span>
                            )}
                          </span>
                          <span className="text-stone-600">
                            {formatBRL(it.price)} · <strong>{formatBRL(it.price * it.quantity)}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
